import React, { useMemo } from 'react';
import SectionCard from '../controls/SectionCard';
import Row from '../controls/Row';
import NumberInput from '../controls/NumberInput';
import Slider from '../controls/Slider';
import Toggle from '../controls/Toggle';
import Segmented from '../controls/Segmented';
import ColorPanel from '../../ColorPanel/ColorPanel';
import { normalizePaint } from '../../../utils/paintUtils';

const ShapeStyleSection = ({
  model,
  palette,
  brandColors,
  onUsePaint,
  onRequestEyedropper,
  disabled,
}) => {
  const selectedElements = model.selectedElements || [];
  const lineLikeSelection = selectedElements.length > 0
    && selectedElements.every((el) => ['line', 'arrow'].includes(el.shapeType));
  const arrowOnly = selectedElements.length > 0
    && selectedElements.every((el) => el.shapeType === 'arrow');
  const supportsFill = !lineLikeSelection;
  const radiusEligible = ['rect', 'frame'];
  const supportsRadius = supportsFill
    && selectedElements.every((el) => radiusEligible.includes(el.shapeType));

  const fillPaint = model.mixed('style.fillPaint');
  const strokePaint = model.mixed('style.strokePaint');
  const strokeWidth = model.mixed('strokeWidth');
  const radius = model.mixed('radius');
  const fillEnabled = model.mixed('fillEnabled');
  const strokeEnabled = model.mixed('strokeEnabled');
  const strokeStyle = model.mixed('strokeStyle');
  const lineCap = model.mixed('lineCap');
  const arrowHead = model.mixed('arrowHead');
  const arrowHeadSize = model.mixed('arrowHeadSize');
  const flipX = model.mixed('flipX');
  const flipY = model.mixed('flipY');

  const applyFillPaint = (paint) => {
    const normalized = normalizePaint(paint, '#e1dccf');
    model.applyToSelection((el) => ({
      style: { fillPaint: normalized },
      fill: normalized.type === 'solid' ? normalized.color : el.fill,
    }), 'debounced', 'shape-fill');
  };

  const applyStrokePaint = (paint) => {
    const normalized = normalizePaint(paint, '#c9a962');
    model.applyToSelection((el) => ({
      style: { strokePaint: normalized },
      stroke: normalized.type === 'solid' ? normalized.color : el.stroke,
    }), 'debounced', 'shape-stroke');
  };

  const handleToggle = (field, current) => {
    const nextValue = current.kind === 'mixed' ? true : !current.value;
    model.applyToSelection({ [field]: nextValue }, 'immediate', `${field}-toggle`);
  };

  const lineStyleOptions = useMemo(
    () => [
      { label: 'Solid', value: 'solid' },
      { label: 'Dashed', value: 'dashed' },
      { label: 'Dotted', value: 'dotted' },
    ],
    [],
  );

  const capOptions = useMemo(
    () => [
      { label: 'Round', value: 'round' },
      { label: 'Square', value: 'square' },
    ],
    [],
  );

  const arrowOptions = useMemo(
    () => [
      { label: 'None', value: 'none' },
      { label: 'Start', value: 'start' },
      { label: 'End', value: 'end' },
      { label: 'Both', value: 'both' },
    ],
    [],
  );

  return (
    <SectionCard title={lineLikeSelection ? 'Line' : 'Shape'}>
      {supportsFill && (
        <Row label="Fill">
          <Toggle
            checked={Boolean(fillEnabled.value)}
            indeterminate={fillEnabled.kind === 'mixed'}
            onChange={() => handleToggle('fillEnabled', fillEnabled)}
            label="Fill"
            disabled={disabled}
          />
          {Boolean(fillEnabled.value) && (
            <ColorPanel
              label="Fill"
              value={fillPaint.value}
              mixed={fillPaint.kind === 'mixed'}
              allowGradient
              documentColors={palette?.documentColors}
              recentColors={palette?.recentColors}
              recentGradients={palette?.recentGradients}
              brandColors={brandColors}
              onChange={applyFillPaint}
              onCommit={applyFillPaint}
              onUsePaint={onUsePaint}
              onRequestEyedropper={
                onRequestEyedropper
                  ? () => onRequestEyedropper((paint) => applyFillPaint(paint))
                  : undefined
              }
              disabled={disabled}
            />
          )}
        </Row>
      )}

      <Row label="Stroke">
        <Toggle
          checked={Boolean(strokeEnabled.value)}
          indeterminate={strokeEnabled.kind === 'mixed'}
          onChange={() => handleToggle('strokeEnabled', strokeEnabled)}
          label="Stroke"
          disabled={disabled}
        />
        {Boolean(strokeEnabled.value) && (
          <ColorPanel
            label="Stroke"
            value={strokePaint.value}
            mixed={strokePaint.kind === 'mixed'}
            allowGradient
            documentColors={palette?.documentColors}
            recentColors={palette?.recentColors}
            recentGradients={palette?.recentGradients}
            brandColors={brandColors}
            onChange={applyStrokePaint}
            onCommit={applyStrokePaint}
            onUsePaint={onUsePaint}
            onRequestEyedropper={
              onRequestEyedropper
                ? () => onRequestEyedropper((paint) => applyStrokePaint(paint))
                : undefined
            }
            disabled={disabled}
          />
        )}
      </Row>

      <Row label="Stroke width">
        <NumberInput
          value={strokeWidth.value ?? 2}
          mixed={strokeWidth.kind === 'mixed'}
          min={0}
          max={50}
          onCommit={(value) => model.applyToSelection({ strokeWidth: value }, 'debounced', 'stroke-width')}
          disabled={disabled}
        />
      </Row>

      {lineLikeSelection && (
        <>
          <Row label="Style">
            <Segmented
              options={lineStyleOptions}
              value={strokeStyle.kind === 'mixed' ? 'solid' : strokeStyle.value || 'solid'}
              onChange={(value) => model.applyToSelection({ strokeStyle: value }, 'immediate', 'stroke-style')}
              disabled={disabled}
            />
          </Row>
          <Row label="Cap">
            <Segmented
              options={capOptions}
              value={lineCap.kind === 'mixed' ? 'round' : lineCap.value || 'round'}
              onChange={(value) => model.applyToSelection({ lineCap: value }, 'immediate', 'line-cap')}
              disabled={disabled}
            />
          </Row>
        </>
      )}

      {arrowOnly && (
        <>
          <Row label="Arrowheads">
            <Segmented
              options={arrowOptions}
              value={arrowHead.kind === 'mixed' ? 'end' : arrowHead.value || 'end'}
              onChange={(value) => model.applyToSelection({ arrowHead: value }, 'immediate', 'arrow-head')}
              disabled={disabled}
            />
          </Row>
          <Row label="Head size">
            <Slider
              min={6}
              max={60}
              step={1}
              value={arrowHeadSize.kind === 'mixed' ? 16 : arrowHeadSize.value || 16}
              mixed={arrowHeadSize.kind === 'mixed'}
              onChange={(value) => model.applyToSelection({ arrowHeadSize: value }, 'commitOnRelease', 'arrow-size')}
              onCommit={() => model.commitPending('arrow-size')}
              disabled={disabled}
            />
          </Row>
        </>
      )}

      {supportsRadius && (
        <Row label="Corner radius">
          <Slider
            min={0}
            max={120}
            step={1}
            value={radius.kind === 'mixed' ? 0 : radius.value}
            mixed={radius.kind === 'mixed'}
            onChange={(value) => model.applyToSelection({ radius: value }, 'commitOnRelease', 'shape-radius')}
            onCommit={() => model.commitPending('shape-radius')}
            disabled={disabled}
          />
        </Row>
      )}

      <Row label="Flip">
        <div className="studio-properties-toggle-group">
          <Toggle
            checked={Boolean(flipX.value)}
            indeterminate={flipX.kind === 'mixed'}
            onChange={() => handleToggle('flipX', flipX)}
            label="Flip H"
            disabled={disabled}
          />
          <Toggle
            checked={Boolean(flipY.value)}
            indeterminate={flipY.kind === 'mixed'}
            onChange={() => handleToggle('flipY', flipY)}
            label="Flip V"
            disabled={disabled}
          />
        </div>
      </Row>
    </SectionCard>
  );
};

export default ShapeStyleSection;
